import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { StatusTag } from "../../../components/ui";

const POSITIONS = ["All positions", "Senior React Developer", "Backend Engineer (Java)", "Data Scientist", "DevOps SRE"];
const STATUSES = ["All statuses", "Confirmed", "Pending", "Declined", "Completed", "Expired"];
const CHANNELS = ["All channels", "Email", "WhatsApp", "Both"];

const isoDays = (d) => new Date(Date.now() + d * 86400000).toISOString();

const SEED = [
  { id: 1, name: "Candidate A", email: "candidate200@example.com", phone: "+91 98765 43210", position: "Senior React Developer", channel: "Both", sentAt: isoDays(-3), opensAt: isoDays(-3), expiresAt: isoDays(2), status: "Confirmed", canExtend: true },
  { id: 2, name: "Candidate B", email: "candidate201@example.com", phone: "+91 91234 56780", position: "Senior React Developer", channel: "Email", sentAt: isoDays(-5), opensAt: isoDays(-5), expiresAt: isoDays(-1), status: "Completed" },
  { id: 3, name: "Candidate C", email: "candidate202@example.com", phone: "+91 99887 66554", position: "Backend Engineer (Java)", channel: "WhatsApp", sentAt: isoDays(-2), opensAt: isoDays(-2), expiresAt: isoDays(0.4), status: "Pending", canExtend: true, canRemind: true },
  { id: 4, name: "Candidate D", email: "candidate204@example.com", phone: "+91 90000 11122", position: "Data Scientist", channel: "Email", sentAt: isoDays(-1), opensAt: isoDays(-1), expiresAt: isoDays(2), status: "Pending", canRemind: true },
  { id: 5, name: "Candidate E", email: "candidate206@example.com", phone: "", position: "DevOps SRE", channel: "Email", sentAt: isoDays(-4), opensAt: isoDays(-4), expiresAt: isoDays(-2), status: "Declined", canUndo: true },
  { id: 6, name: "Candidate F", email: "candidate208@example.com", phone: "", position: "Data Scientist", channel: "Email", sentAt: isoDays(-6), opensAt: isoDays(-6), expiresAt: isoDays(-3), status: "Expired" },
  { id: 7, name: "Candidate G", email: "candidate209@example.com", phone: "+91 88776 65543", position: "Backend Engineer (Java)", channel: "Both", sentAt: isoDays(-1.5), opensAt: isoDays(-1.5), expiresAt: isoDays(1.5), status: "Confirmed", canExtend: true },
  { id: 8, name: "Candidate H", email: "candidate210@example.com", phone: "+91 77665 54432", position: "Senior React Developer", channel: "WhatsApp", sentAt: isoDays(-0.5), opensAt: isoDays(-0.5), expiresAt: isoDays(2.5), status: "Pending", canRemind: true },
];

const channelTag = (c) => c === "Email" ? <StatusTag type="blue" text="Email" /> : c === "WhatsApp" ? <StatusTag type="green" text="WA" /> : <StatusTag type="indigo" text="Both" />;
const statusTag = (s) => {
  const map = { Confirmed: "green", Pending: "amber", Declined: "red", Completed: "violet", Expired: "slate" };
  return <StatusTag type={map[s] || "slate"} text={s} />;
};
const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};
const isExpiringSoon = (iso) => {
  if (!iso) return false;
  const ms = new Date(iso).getTime() - Date.now();
  return ms > 0 && ms < 24 * 3600000;
};

const initials = (name) => (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
const avatarColor = (id) => ["#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#7B5AC4"][(id || 0) % 6];

const PAGE_SIZE = 10;

const TrackInvites = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(SEED);
  const [filters, setFilters] = useState({ position: "All positions", status: "All statuses", channel: "All channels", from: "", to: "" });
  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => { document.title = "Track invites - Ample Hire"; }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filters.position !== "All positions" && r.position !== filters.position) return false;
      if (filters.status !== "All statuses" && r.status !== filters.status) return false;
      if (filters.channel !== "All channels" && r.channel !== filters.channel) return false;
      if (filters.from && new Date(r.sentAt) < new Date(filters.from)) return false;
      if (filters.to && new Date(r.sentAt) > new Date(filters.to + "T23:59:59")) return false;
      return true;
    });
  }, [rows, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const startEdit = (id, currentExpires) => {
    setEditingId(id);
    setEditDate(new Date(currentExpires).toISOString().slice(0, 16));
  };
  const saveEdit = (id) => {
    setRows((rs) => rs.map((r) => r.id === id ? { ...r, expiresAt: new Date(editDate).toISOString() } : r));
    setEditingId(null);
  };

  const remind = (id) => {
    alert(`Stub: reminder sent to invite #${id} via the original channel.`);
  };
  const undo = (id) => {
    setRows((rs) => rs.map((r) => r.id === id ? { ...r, status: "Pending", canUndo: false } : r));
  };
  const cancelInvite = (id) => {
    if (!window.confirm("Cancel this invitation?")) return;
    setRows((rs) => rs.map((r) => r.id === id ? { ...r, status: "Declined", canUndo: true } : r));
  };

  const toggleSelect = (id) => {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    if (selected.size === pageRows.length && pageRows.length > 0) setSelected(new Set());
    else setSelected(new Set(pageRows.map((r) => r.id)));
  };
  const bulkRemind = () => alert(`Stub: reminders sent to ${selected.size} invite(s).`);
  const bulkExtend = () => alert(`Stub: 48h extension granted to ${selected.size} invite(s).`);
  const bulkCancel = () => {
    if (!window.confirm(`Cancel ${selected.size} invite(s)?`)) return;
    setRows((rs) => rs.map((r) => selected.has(r.id) ? { ...r, status: "Declined", canUndo: true } : r));
    setSelected(new Set());
  };

  return (
    <div className="app-content page-enter" style={{ maxWidth: 1280, margin: "0 auto", padding: "20px 24px 32px" }}>
      <div style={hdrRow}>
        <div>
          <h1 style={hdrTitle}>Track invites</h1>
          <p style={hdrSub}>{filtered.length} active invitations across all positions — monitor status and validity</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={bulkExtend} disabled={selected.size === 0} style={{ ...btnGhost, opacity: selected.size === 0 ? 0.5 : 1 }}>Extend ({selected.size})</button>
          <button onClick={bulkCancel} disabled={selected.size === 0} style={{ ...btnGhost, opacity: selected.size === 0 ? 0.5 : 1 }}>Cancel selected</button>
          <button onClick={bulkRemind} disabled={selected.size === 0} style={{ ...btnPrimary, opacity: selected.size === 0 ? 0.5 : 1 }}>Send reminder</button>
          <button onClick={() => navigate("/admin/invite-candidates")} style={btnPrimary}>+ New invite</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, padding: 12, marginBottom: 12, display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr", gap: 10 }}>
        <Field label="Position">
          <select value={filters.position} onChange={(e) => { setFilters({ ...filters, position: e.target.value }); setPage(1); }} style={input}>
            {POSITIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }} style={input}>
            {STATUSES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Channel">
          <select value={filters.channel} onChange={(e) => { setFilters({ ...filters, channel: e.target.value }); setPage(1); }} style={input}>
            {CHANNELS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="From"><input type="date" value={filters.from} onChange={(e) => { setFilters({ ...filters, from: e.target.value }); setPage(1); }} style={input} /></Field>
        <Field label="To"><input type="date" value={filters.to} onChange={(e) => { setFilters({ ...filters, to: e.target.value }); setPage(1); }} style={input} /></Field>
      </div>

      {/* Table */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#F8FAFC" }}>
            <tr>
              <th style={{ ...th, width: 36 }}>
                <input type="checkbox" checked={pageRows.length > 0 && selected.size === pageRows.length} onChange={toggleAll} style={{ accentColor: "#7B5AC4" }} />
              </th>
              <th style={th}>Candidate</th>
              <th style={th}>Position</th>
              <th style={th}>Channel</th>
              <th style={th}>Sent</th>
              <th style={th}>Expires</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>No invitations match the filters.</td></tr>
            )}
            {pageRows.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                <td style={td}>
                  <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} style={{ accentColor: "#7B5AC4" }} />
                </td>
                <td style={td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 15, background: avatarColor(r.id), color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11 }}>
                      {initials(r.name)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: "#0F172A" }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: "#64748B" }}>{r.phone || r.email}</div>
                    </div>
                  </div>
                </td>
                <td style={{ ...td, color: "#475569" }}>{r.position}</td>
                <td style={td}>{channelTag(r.channel)}</td>
                <td style={{ ...td, fontSize: 12, color: "#64748B" }}>{fmt(r.sentAt)}</td>
                <td style={td}>
                  {editingId === r.id ? (
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <input type="datetime-local" value={editDate} onChange={(e) => setEditDate(e.target.value)} style={{ ...input, fontSize: 11, padding: "5px 8px", width: "auto" }} />
                      <button onClick={() => saveEdit(r.id)} style={{ padding: "4px 10px", fontSize: 11, borderRadius: 4, border: "none", background: "#7B5AC4", color: "white", fontWeight: 600, cursor: "pointer" }}>Save</button>
                      <button onClick={() => setEditingId(null)} style={{ padding: "4px 8px", fontSize: 11, borderRadius: 4, border: "1px solid #E2E8F0", background: "white", color: "#475569", cursor: "pointer" }}>×</button>
                    </div>
                  ) : (
                    <div onClick={() => r.canExtend && startEdit(r.id, r.expiresAt)}
                      style={{ fontSize: 12, color: isExpiringSoon(r.expiresAt) ? "#DC2626" : "#64748B", cursor: r.canExtend ? "pointer" : "default", fontWeight: isExpiringSoon(r.expiresAt) ? 600 : 400 }}
                      title={r.canExtend ? "Click to extend" : ""}>
                      {fmt(r.expiresAt)}
                      {isExpiringSoon(r.expiresAt) && " ⚠"}
                    </div>
                  )}
                </td>
                <td style={td}>{statusTag(r.status)}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  {r.status === "Pending" && r.canRemind && <button onClick={() => remind(r.id)} style={actionBtn}>Remind</button>}
                  {r.canExtend && r.status === "Confirmed" && <button onClick={() => startEdit(r.id, r.expiresAt)} style={actionBtn}>Extend</button>}
                  {r.canUndo && <button onClick={() => undo(r.id)} style={actionBtn}>Undo</button>}
                  {r.status === "Completed" && <button onClick={() => navigate(`/admin/candidate-report?candidateId=${r.userId ?? r.candidateId ?? r.id}`)} style={actionBtn}>View</button>}
                  {r.status === "Pending" && <button onClick={() => cancelInvite(r.id)} style={{ ...actionBtn, color: "#DC2626", borderColor: "#FCA5A5" }}>Cancel</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: 12, color: "#64748B" }}>
          <span>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} style={{ ...pageBtn, opacity: page === 1 ? 0.4 : 1 }}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} style={{
                ...pageBtn,
                background: p === page ? "#7B5AC4" : "white",
                color: p === page ? "white" : "#475569",
                borderColor: p === page ? "#7B5AC4" : "#E2E8F0",
                fontWeight: p === page ? 700 : 500,
              }}>{p}</button>
            ))}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} style={{ ...pageBtn, opacity: page === totalPages ? 0.4 : 1 }}>›</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 10, color: "#64748B", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
    {children}
  </div>
);

const input = { width: "100%", padding: "7px 10px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12, color: "#0F172A", outline: "none", background: "white", boxSizing: "border-box" };
const hdrRow = { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16, gap: 16 };
const hdrTitle = { fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 };
const hdrSub = { fontSize: 13, color: "#64748B", margin: "4px 0 0" };
const btnPrimary = { padding: "8px 14px", borderRadius: 6, background: "#7B5AC4", color: "white", border: "none", fontWeight: 600, fontSize: 12, cursor: "pointer" };
const btnGhost = { padding: "8px 14px", borderRadius: 6, background: "white", color: "#475569", border: "1px solid #E2E8F0", fontWeight: 600, fontSize: 12, cursor: "pointer" };
const th = { textAlign: "left", padding: "10px 14px", fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, borderBottom: "1px solid #E2E8F0" };
const td = { padding: "10px 14px", verticalAlign: "middle" };
const actionBtn = { padding: "5px 12px", fontSize: 11, borderRadius: 4, border: "1px solid #E2E8F0", background: "white", color: "#475569", fontWeight: 600, cursor: "pointer", marginLeft: 4 };
const pageBtn = { width: 30, height: 30, borderRadius: 6, border: "1px solid #E2E8F0", background: "white", color: "#475569", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" };

export default TrackInvites;
